/**
 * Compress a PDF in the browser with Ghostscript (WASM) before upload.
 *
 * PDF compression used to run server-side in the Cloud Run media-transcoder
 * (native Ghostscript). It now runs here, in the browser, using the same
 * Ghostscript `/ebook` profile compiled to WebAssembly — so the uploaded object
 * is already the final, smaller file and never needs an async transcode pass
 * (a PDF publishes immediately instead of sitting in PROCESSING).
 *
 * `/ebook` is what produced the 80%+ reductions server-side: it downsamples
 * embedded images to ~150dpi, subsets fonts, and recompresses content streams.
 * Nothing pdf-lib-based matches it — hence the real engine.
 *
 * The ~16MB Ghostscript wasm and its glue are loaded from a public CDN at
 * runtime, NOT bundled or committed — they download once (immutably cached by
 * the browser) the first time a user compresses a PDF this session. The wasm is
 * fetched by Emscripten itself via `locateFile`: this build has no `wasmBinary`
 * code path, and its default `scriptDirectory` resolves to the app origin (whose
 * SPA fallback returns index.html — a `<!doctype…>` that fails wasm compile), so
 * `locateFile` MUST point at the absolute CDN URL.
 *
 * Best-effort by design: if the engine fails to load (offline, CSP), the file
 * is unusual, or compression gains nothing, the original file is returned.
 * Compression is never allowed to block or fail an upload.
 *
 * NOTE: Ghostscript is AGPL-3.0 (same license as the native binary previously
 * run server-side). To repoint at a self-hosted copy, change `GS_CDN` below.
 */

/** Pinned Ghostscript-WASM build on jsDelivr (npm mirror). */
const GS_CDN = 'https://cdn.jsdelivr.net/npm/@jspawn/ghostscript-wasm@0.0.2';

/** Ghostscript `/ebook` — downsamples embedded images to ~150dpi and re-encodes. */
const GS_ARGS = [
  '-sDEVICE=pdfwrite',
  '-dCompatibilityLevel=1.4',
  '-dPDFSETTINGS=/ebook',
  '-dNOPAUSE',
  '-dQUIET',
  '-dBATCH',
  '-dSAFER',
];

interface GhostscriptModule {
  FS: {
    writeFile(path: string, data: Uint8Array): void;
    readFile(path: string): Uint8Array;
  };
  callMain(args: string[]): number | Promise<number>;
}

type GhostscriptFactory = (config?: Record<string, unknown>) => Promise<GhostscriptModule>;

/**
 * Load (once per session) the Ghostscript wasm glue from the CDN. The promise is
 * cached so the glue module is imported a single time; a failure clears the
 * cache so the next PDF can retry. The wasm binary itself is fetched by
 * Emscripten via `locateFile` and cached by the browser (immutable CDN URL).
 */
let gluePromise: Promise<GhostscriptFactory> | null = null;

const loadGhostscript = (): Promise<GhostscriptFactory> => {
  if (!gluePromise) {
    gluePromise = import(/* @vite-ignore */ `${GS_CDN}/gs.mjs`)
      .then(module => module.default as GhostscriptFactory)
      .catch(error => {
        gluePromise = null;
        throw error;
      });
  }
  return gluePromise;
};

export const compressPdfFile = async (file: File): Promise<File> => {
  if (file.type !== 'application/pdf') {
    return file;
  }

  try {
    const createGs = await loadGhostscript();

    // Fresh module per run (Emscripten main() runs once per instance). locateFile
    // points the wasm fetch at the CDN — without it Emscripten fetches gs.wasm
    // from the app origin and compiles the SPA's index.html.
    const gs = await createGs({
      locateFile: (path: string) => (path.endsWith('.wasm') ? `${GS_CDN}/gs.wasm` : path),
      noExitRuntime: true,
      print: () => undefined,
      printErr: () => undefined,
    });

    gs.FS.writeFile('input.pdf', new Uint8Array(await file.arrayBuffer()));
    const status = await gs.callMain([...GS_ARGS, '-sOutputFile=output.pdf', 'input.pdf']);
    if (status !== 0) {
      throw new Error(`ghostscript exited ${status}`);
    }
    const output = gs.FS.readFile('output.pdf');

    // Ghostscript can occasionally produce a larger file than the source (already
    // optimised PDFs); never upload something bigger than what the user picked.
    if (output.byteLength >= file.size) {
      return file;
    }

    // Copy into a fresh ArrayBuffer before constructing the File — the Emscripten
    // Uint8Array is typed over ArrayBufferLike, which TS 5.7+ rejects as a
    // BlobPart; a plain ArrayBuffer is valid across all versions.
    const buffer = new ArrayBuffer(output.byteLength);
    new Uint8Array(buffer).set(output);

    return new File([buffer], file.name, {
      type: 'application/pdf',
      lastModified: file.lastModified,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[PDF_COMPRESS] "${file.name}": compression failed, uploading original`, error);
    return file;
  }
};
